/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import highlight from 'flarum/common/helpers/highlight';
import icon from 'flarum/common/helpers/icon';
import extractText from 'flarum/common/utils/extractText';
import LinkButton from 'flarum/common/components/LinkButton';
import app from 'flarum/forum/app';
import type { SearchSource } from 'flarum/forum/components/Search';
import type Mithril from 'mithril';
import articleSearch, { PAGE_SIZE, setCachedArticleSearch } from '../search';
import ArticleListItem from './ArticleListItem';
import type Article from '../models/Article';

export { PAGE_SIZE } from '../search';
export const MAX_DISP_ITEMS = 5;

export default class ArticleSearchSource implements SearchSource {
	/** Keyed by lowercased query; `false` when the query returned nothing at all. */
	protected results: Record<string, Article[] | false> = {};

	async search(query: string): Promise<void> {
		const { articles, isFirstPage, nextPage } = await articleSearch(query, [], 0, PAGE_SIZE);

		this.results[query] = articles;
		m.redraw();

		setCachedArticleSearch({ articles, isFirstPage, nextPage, q: query });
	}

	view(query: string): Array<Mithril.Vnode> {
		query = query.toLowerCase();

		const results = this.manipulateResults(this.results[query] || []).slice(0, MAX_DISP_ITEMS);

		return [
			<li className="Dropdown-header">
				{icon('fas fa-book')} {app.translator.trans('glowingblue-orell-articles.forum.search.header')}
			</li>,
			<li>
				<LinkButton icon="fas fa-search" href={app.route.of_articles(query)}>
					{highlight(extractText(app.translator.trans('glowingblue-orell-articles.forum.search.all_articles_button', { query })), query)}
				</LinkButton>
			</li>,
			...results.map((article) => <ArticleListItem article={article} searchQuery={query} />),
		];
	}

	/**
	 * Hook for extensions that need to reorder or filter the results before render.
	 */
	manipulateResults(results: Article[]): Article[] {
		return results;
	}
}
